// resolving regression issue so disabled it for now
/* eslint-disable no-nested-ternary */

import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";

import { CircularProgress, Stack } from "@mui/material";

import { useFlags } from "launchdarkly-react-client-sdk";

import EmployeeTimeOffReqTable from "components/Screens/Timeoff/TimeoffAdmin/EmployeeTimeOffDetails/EmployeeTimeOffReqTable";
import {
  getEmployeeTimeOffDetails,
  getEmployeeCustomerOptions,
} from "components/Screens/Timeoff/TimeoffAdmin/TimeOffAdminServices/EmployeeTimeOffDetailsService";
import { NEW_ACTIVITY } from "components/Screens/Timeoff/TimeoffAdmin/EmployeeTimeoffNewActivity/Enum";
import { isLastAssignmentView } from "components/Screens/Timeoff/TimeoffAdmin/EmployeeTimeOffDetails/lastAssignmentView";
import EmployeeTimeOffDetails from "components/Screens/Timeoff/TimeoffAdmin/EmployeeTimeoffNewActivity/EmployeeTimeOffDetails";

import ToolBar from "components/Widgets/PageToolBarV2";
import CustomDropdown from "components/common/Dropdown";

import { Employee_TIMEOFF_REQUESTS as employeeTimeoffRequests, getStartDateAndEndDate } from "constants";
import { excludeCancelledFromAll } from "util/timeoffRequests";

import {
  leavesTypesDropdown,
  employeeRequestsYearDropdown,
  ALL_CUSTOMERS_OPTION,
} from "components/Screens/Timeoff/TimeoffAdmin/Constants";

import NothingToSeeIcon from "components/Screens/Timeoff/TimeoffAdmin/NothingToSeeIcon";
import CalendarIcon from "components/Screens/Timeoff/TimeoffAdmin/EmployeeTimeOffDetails/Icons/CalendarIcon";

function EmployeeTimeOffRequests(props) {
  const { setScreenView, screenView, leaveTakenSuccessfully } = props;

  const { anniversaryBasedTimeOff } = useFlags();

  const [currentTab, setCurrentTab] = useState("All");

  const [selectedFilter, setSelectedFilter] = useState({
    selectedLeaveType: "",
    year: "thisYear",
    customerId: "",
  });
  const [customerOptions, setCustomerOptions] = useState([ALL_CUSTOMERS_OPTION]);
  const [timeOffTypes, setTimeOffTypes] = useState([]);
  const [tabs, setTabs] = useState(employeeTimeoffRequests);
  const [employeeTimeOffDetails, setEmployeeTimeOffDetails] = useState([]);
  const [employeeSelectedActivity, setEmployeeSelectedActivity] = useState(null);
  const [timeOffDetailsLoading, setTimeOffDetailsLoading] = useState(false);

  // redux
  // redux state
  const leaveTypes = useSelector((state) => state?.TimeoffLeaveTypes?.leaveTypes);
  // const [screenView, setScreenView] = useState(NEW_ACTIVITY.ALL_EMPLOYEES_REQUESTS);
  const getEmployeeDetails = useSelector((state) => state.GetEmployeeDetails.getEmployeeDetails);
  // PRD-3255: unassigned and inactive talents can browse their request history but not act on it
  const isReadOnlyView = isLastAssignmentView(getEmployeeDetails?.payload?.status);

  // Cancelled requests belong on the Cancelled tab only. The raw `all` list is
  // still what the details view pages through, so opening a cancelled request
  // from the Cancelled tab keeps landing on the right one.
  const requestsByTab = useMemo(() => excludeCancelledFromAll(employeeTimeOffDetails), [employeeTimeOffDetails]);

  const renderToolBar = () => {
    return (
      <Stack
        direction="row"
        sx={{
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: 1,
          paddingBottom: "16px",

          "@media (max-width: 1200px)": {
            flexDirection: "column",
          }
        }}>
        <ToolBar
          updateHash={false}
          tabs={tabs || []}
          currentTab={currentTab}
          setCurrentTab={setCurrentTab}
          styles={{
            margin: "0px",
            padding: "0px",
            maxWidth: "fit-content !important",
          }}
          toolBarWrapperStyles={{
            borderRadius: "19.738px",
            border: "1px solid var(--color-primitives-primary-100)",
            background: "#F8F6FF",
            padding: "0px 1px",
            overflowX: "scroll",
            "@media (max-width: 850px)": {
              overflowX: "scroll",
            },
          }}
        />
        <Stack direction="row" spacing={1}>
          <CustomDropdown
            options={customerOptions}
            onSelect={(key) => {
              setSelectedFilter({
                ...selectedFilter,
                customerId: key,
              });
            }}
            placeholder="Customers"
            buttonLabel={customerOptions?.find((customer) => customer?.key === selectedFilter?.customerId)?.label}
          />
          <CustomDropdown
            options={employeeRequestsYearDropdown}
            onSelect={(key) => {
              setSelectedFilter({
                ...selectedFilter,
                year: key,
              });
            }}
            buttonLabel={employeeRequestsYearDropdown?.find((leave) => leave?.key === selectedFilter?.year)?.label}
            // style={{ width: "225px" }}
          />
          <CustomDropdown
            options={timeOffTypes}
            onSelect={(key) => {
              setSelectedFilter({
                ...selectedFilter,
                selectedLeaveType: key,
              });
            }}
            placeholder="Type"
            onClear={() =>
              setSelectedFilter({
                ...selectedFilter,
                selectedLeaveType: "",
              })
            }
            buttonLabel={timeOffTypes?.find((leave) => leave?.key === selectedFilter?.selectedLeaveType)?.label || ""}
            // style={{ width: "177px" }}
          />
        </Stack>
      </Stack>
    );
  };

  const fetchEmployeesTimeOffDetails = async () => {
    setTimeOffDetailsLoading(true);
    try {
      const dateRange = getStartDateAndEndDate(selectedFilter?.year);
      const payload = {
        leaveTypeId: selectedFilter?.selectedLeaveType || "",
        country: selectedFilter?.country || "",
        year: selectedFilter?.year || "",
        customerId: selectedFilter?.customerId || "",
        flag: selectedFilter?.timeOffFlag || "",
        employeeId: getEmployeeDetails.payload?.employeeId,
        startDate: dateRange?.startDate,
        endDate: dateRange?.endDate,
        anniversaryBasedTimeOff,
      };

      const response = await getEmployeeTimeOffDetails(payload);
      if (response?.success) {
        setTimeOffDetailsLoading(false);
        const requestsForTabs = excludeCancelledFromAll(response?.data);
        const allTabsCount = employeeTimeoffRequests.map((activity) => ({
          ...activity,
          count: (requestsForTabs[activity?.title?.toLowerCase()] || [])?.length,
        }));
        setTabs(allTabsCount || employeeTimeoffRequests);
        setEmployeeTimeOffDetails(response?.data);
      }
    } catch (error) {
      setTimeOffDetailsLoading(false);
    }
  };

  useEffect(() => {
    if (timeOffTypes?.length && getEmployeeDetails.payload?.employeeId) fetchEmployeesTimeOffDetails();
  }, [selectedFilter, timeOffTypes, getEmployeeDetails.payload?.employeeId, leaveTakenSuccessfully]);

  useEffect(() => {
    setTimeOffTypes(leavesTypesDropdown);
  }, [leaveTypes]);

  useEffect(() => {
    const employeeId = getEmployeeDetails.payload?.employeeId;
    if (!employeeId) return;
    const fetchCustomerOptions = async () => {
      const options = await getEmployeeCustomerOptions(employeeId);
      setCustomerOptions(options);
    };
    fetchCustomerOptions();
  }, [getEmployeeDetails.payload?.employeeId]);

  return (
    <div className="custom-scroll timeoff-view">
      {screenView === NEW_ACTIVITY.ALL_EMPLOYEES_REQUESTS ? (
        <>
          <div style={{ paddingBottom: "0px" }}>
            <Stack
              direction="row"
              spacing="12px"
              sx={{
                alignItems: "center",
                justifyContent: "flex-start",
                mb: "18px"
              }}>
              <CalendarIcon />
              <div className="timeoff-subheading upcoming-leaves-margin">
                Requests ({requestsByTab?.all?.length || 0})
              </div>
            </Stack>
          </div>
          {renderToolBar()}

          {timeOffDetailsLoading ? (
            <Stack sx={{ height: "calc(100vh - 675px)", justifyContent: "center", alignItems: "center" }}>
              <CircularProgress />
            </Stack>
          ) : requestsByTab?.[currentTab?.toLowerCase()]?.length ? (
            <EmployeeTimeOffReqTable
              employeeTimeOffReq={requestsByTab[currentTab?.toLowerCase()] || []}
              totalCount={requestsByTab?.all?.length || 0}
              setScreenView={setScreenView}
              setEmployeeSelectedActivity={setEmployeeSelectedActivity}
            />
          ) : (
            <Stack sx={{ m: "auto", alignItems: "center", justifyContent: "center", height: "30vh" }}>
              <NothingToSeeIcon />
              <Stack sx={{ textAlign: "center" }}>
                No Results found. <br />
              </Stack>
            </Stack>
          )}
        </>
      ) : (
        <EmployeeTimeOffDetails
          employeesAllRequests={employeeTimeOffDetails?.all}
          employeeSelectedActivity={employeeSelectedActivity}
          readOnly={isReadOnlyView}
          backToAllHolidays={() => {
            setScreenView(NEW_ACTIVITY.ALL_EMPLOYEES_REQUESTS);
            setEmployeeSelectedActivity(null);
            fetchEmployeesTimeOffDetails();
          }}
          onSuccessCancelLeave={() => {
            fetchEmployeesTimeOffDetails();
            setScreenView(NEW_ACTIVITY.ALL_EMPLOYEES_REQUESTS);
            setEmployeeSelectedActivity(null);
          }}
        />
      )}
    </div>
  );
}

export default EmployeeTimeOffRequests;
